import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';
import type { CrmFeedbackType } from '../../types/crmFeedback';
import { useFeedback } from './FeedbackProvider';
import './FeedbackModal.css';

const FeedbackModal: React.FC = () => {
  const {
    isOpen,
    capturing,
    capture,
    submitting,
    error,
    close,
    submit,
    setScreenshot,
  } = useFeedback();
  const [type, setType] = useState<CrmFeedbackType>('bug');
  const [description, setDescription] = useState('');
  const [includeLogs, setIncludeLogs] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setType('bug');
      setDescription('');
      setIncludeLogs(false);
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) close();
    };
    document.addEventListener('keydown', onKey);
    const focusTimer = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(focusTimer);
    };
  }, [isOpen, close, submitting]);

  if (!isOpen && !capturing) return null;

  if (capturing || !capture) {
    return (
      <div className="crm-feedback-modal-overlay" role="presentation">
        <div className="crm-feedback-modal crm-feedback-modal--busy" role="status">
          <Loader2 size={18} className="crm-feedback-spin" aria-hidden />
          Capturing screen…
        </div>
      </div>
    );
  }

  const placeholder = type === 'bug' ? 'What happened?' : 'What should we add?';
  const canSubmit = description.trim().length > 0 && !submitting;
  const showScreenshot = !!capture.screenshotDataUrl;
  const logCount = capture.logs.length;

  const onPickFile = (file: File | null) => {
    if (!file || !file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setScreenshot(url, file);
  };

  return (
    <div className="crm-feedback-modal-overlay" role="presentation" onClick={() => !submitting && close()}>
      <div
        className="crm-feedback-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="crm-feedback-title"
        onClick={e => e.stopPropagation()}
      >
        <header className="crm-feedback-modal-header">
          <div>
            <h2 id="crm-feedback-title">Send feedback</h2>
            <p>A screenshot and browser logs help us see exactly what you saw.</p>
          </div>
          <button
            type="button"
            className="crm-feedback-modal-close"
            onClick={close}
            disabled={submitting}
            aria-label="Close"
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="crm-feedback-modal-body">
          <div className="crm-feedback-type" role="tablist" aria-label="Feedback type">
            <button
              type="button"
              role="tab"
              aria-selected={type === 'bug'}
              className={`crm-feedback-type-btn${type === 'bug' ? ' is-active' : ''}`}
              onClick={() => setType('bug')}
            >
              Bug
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={type === 'feature'}
              className={`crm-feedback-type-btn${type === 'feature' ? ' is-active' : ''}`}
              onClick={() => setType('feature')}
            >
              Feature
            </button>
          </div>

          <label className="crm-feedback-field">
            <span className="sr-only">{placeholder}</span>
            <textarea
              ref={textareaRef}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={placeholder}
              rows={4}
              onPaste={e => {
                const item = Array.from(e.clipboardData.items).find(i => i.type.startsWith('image/'));
                const file = item?.getAsFile();
                if (file) onPickFile(file);
              }}
            />
          </label>

          <div className="crm-feedback-meta">
            {showScreenshot ? (
              <div className="crm-feedback-shot">
                <img src={capture.screenshotDataUrl || ''} alt="Captured screen" />
                {type === 'feature' && (
                  <button type="button" className="crm-feedback-shot-remove" onClick={() => setScreenshot(null, null)}>
                    Remove screenshot
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="crm-feedback-attach"
                onClick={() => fileRef.current?.click()}
              >
                <ImagePlus size={14} aria-hidden />
                Attach screenshot
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={e => onPickFile(e.target.files?.[0] ?? null)}
            />

            {type === 'bug' ? (
              <span className="crm-feedback-chip">
                {logCount > 0 ? `${logCount} browser logs attached` : 'No recent browser logs'}
              </span>
            ) : (
              <label className="crm-feedback-check">
                <input
                  type="checkbox"
                  checked={includeLogs}
                  onChange={e => setIncludeLogs(e.target.checked)}
                />
                Include browser logs
              </label>
            )}
          </div>

          {error && <p className="crm-feedback-error" role="alert">{error}</p>}
        </div>

        <footer className="crm-feedback-modal-footer">
          <button type="button" className="crm-feedback-btn-ghost" onClick={close} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="crm-feedback-btn-primary"
            disabled={!canSubmit}
            onClick={() => submit({ type, description, includeLogs: type === 'bug' || includeLogs })}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="crm-feedback-spin" aria-hidden />
                Sending…
              </>
            ) : (
              'Submit'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default FeedbackModal;
